"use client";

import { CSSProperties, useState } from "react";
import { Trash2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { PollBlock as PollBlockType } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { resolvePollAppearance } from "../../../lib/blockRegistry";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import { useResolvedGridGap } from "../../../hooks/useGridGap";
import RichText from "../text/richText";
import RichHtml from "./richHtml";
import { seedTitleHtml, stripHtml } from "../../../lib/titleHtml";
import { fieldTag } from "../../../lib/sectionRegistry";
import {
    useBlockAggregate,
    useSubmitBlockResponse,
} from "../../../api/useBlockResponses";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";

const LETTERS = ["A", "B", "C", "D", "E", "F"];

// Selection accents come from the theme (CSS vars); surface colors resolve via
// the block appearance (theme-following unless the author overrides).
const PRIMARY = "hsl(var(--lomda-primary))";
const CARD_BG = "hsl(var(--lomda-card))";

export default function PollBlock({ block }: { block: PollBlockType }) {
    const locale = useLomdaLocale();
    const questionTag = fieldTag("poll", "question");
    const editable = useBlockEditable();
    const replaceBlockData = useLomdaV3Editor((s) => s.replaceBlockData);
    const gridGap = useResolvedGridGap(block.id);
    const theme = useLomdaV3Editor((s) => s.metadata.theme);
    const { question, options } = block.data;
    const appearance = resolvePollAppearance(block.data.appearance, theme);
    const cardStyle: CSSProperties = {
        backgroundColor: appearance.cardBackground,
        borderColor: appearance.border,
        borderWidth: appearance.borderWidth,
        borderStyle: "solid",
    };

    const lomdaId = useLomdaV3Editor((s) => s.lomdaId);
    const [voted, setVoted] = useState<string | null>(null);
    const submit = useSubmitBlockResponse();
    const aggregate = useBlockAggregate(block.id, !editable && !!voted);

    const vote = (optionId: string) => {
        if (voted) return;
        setVoted(optionId);
        submit.mutate(
            { lomdaId, blockId: block.id, type: "poll", payload: { optionId } },
            { onSuccess: () => aggregate.refetch() }
        );
    };

    const update = (patch: Partial<PollBlockType["data"]>) =>
        replaceBlockData(block.id, { ...block.data, ...patch });

    const letterStyle = (on: boolean): CSSProperties =>
        on
            ? { backgroundColor: PRIMARY, color: CARD_BG }
            : { backgroundColor: appearance.optionBackground, color: PRIMARY };

    if (editable) {
        return (
            <div className="flex flex-col gap-4">
                <RichText
                    initialHtml={seedTitleHtml(question, questionTag)}
                    defaultTag={questionTag}
                    editable
                    useGlobalToolbar
                    blockId={block.id}
                    placeholder={t("lomda-v3-editor.poll.question-placeholder")}
                    onCommit={(html) => update({ question: html })}
                />
                <div className="flex flex-col" style={{ gap: gridGap }}>
                    {options.map((o, i) => (
                        <div
                            key={o.id}
                            className="lomda-v3-card-text flex items-center gap-3 rounded-2xl p-4"
                            style={cardStyle}
                        >
                            <span
                                className="flex size-8 shrink-0 items-center justify-center rounded-full lomda-v3-text-label"
                                style={letterStyle(false)}
                            >
                                {LETTERS[i] ?? i + 1}
                            </span>
                            <div className="min-w-0 flex-1">
                                <RichText
                                    initialHtml={o.text}
                                    editable
                                    useGlobalToolbar
                                    blockId={block.id}
                                    groupKey={`${block.id}:poll-option`}
                                    placeholder={t(
                                        "lomda-v3-editor.poll.option-placeholder"
                                    )}
                                    onCommit={(text) =>
                                        update({
                                            options: options.map((x) =>
                                                x.id === o.id
                                                    ? { ...x, text }
                                                    : x
                                            ),
                                        })
                                    }
                                />
                            </div>
                            <button
                                type="button"
                                aria-label={t("lomda-v3-editor.block.delete")}
                                disabled={options.length <= 2}
                                onClick={() =>
                                    update({
                                        options: options.filter(
                                            (x) => x.id !== o.id
                                        ),
                                    })
                                }
                                className="flex size-7 shrink-0 items-center justify-center rounded text-muted-foreground transition hover:bg-muted hover:text-destructive disabled:opacity-30"
                            >
                                <Trash2 className="size-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    const valid = options.filter((o) => o.text.replace(/<[^>]*>/g, "").trim());
    if (!stripHtml(question) || valid.length === 0) return null;

    // Real results, aggregated across learners (fetched after voting).
    const counts = (aggregate.data?.counts ?? {}) as Record<string, number>;
    const total = Object.values(counts).reduce((sum, n) => sum + n, 0);
    const pct = (id: string) =>
        total > 0 ? Math.round(((counts[id] ?? 0) / total) * 100) : 0;
    const showResults = !!voted && !!aggregate.data;

    return (
        <div className="flex flex-col gap-4">
            <RichHtml html={seedTitleHtml(question, questionTag)} />
            <div className="flex flex-col" style={{ gap: gridGap }}>
                {valid.map((o, i) => {
                    const picked = voted === o.id;
                    const percent = pct(o.id);
                    return (
                        <button
                            key={o.id}
                            type="button"
                            disabled={!!voted}
                            onClick={() => vote(o.id)}
                            className={cn(
                                "lomda-v3-card-text relative flex items-center gap-3 overflow-hidden rounded-2xl p-4 text-start transition disabled:cursor-default",
                                !voted && "hover:brightness-95"
                            )}
                            style={{
                                ...cardStyle,
                                ...(picked ? { borderColor: PRIMARY } : {}),
                            }}
                        >
                            {showResults && (
                                <span
                                    className="absolute inset-y-0 start-0 transition-all duration-700"
                                    style={{
                                        width: `${percent}%`,
                                        backgroundColor: PRIMARY,
                                        opacity: picked ? 0.18 : 0.08,
                                    }}
                                />
                            )}
                            <span
                                className="relative flex size-8 shrink-0 items-center justify-center rounded-full lomda-v3-text-label"
                                style={letterStyle(picked)}
                            >
                                {LETTERS[i] ?? i + 1}
                            </span>
                            <RichHtml
                                html={o.text}
                                className="relative min-w-0 flex-1 lomda-v3-text-p"
                            />
                            {showResults && (
                                <span
                                    className="relative lomda-v3-text-label"
                                    style={{ color: PRIMARY }}
                                >
                                    {percent}%
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
            {voted && (
                <span className="lomda-v3-text-small text-[hsl(var(--lomda-muted-text))] animate-in fade-in">
                    {t("lomda-v3-editor.poll.voted", locale)}
                </span>
            )}
        </div>
    );
}
